import React, { useState } from "react";
import { Trash2, ChevronLeft, ChevronRight, Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useSection } from "./useSection";
import { SaveBar } from "./ui";
import { authFetch } from "../../data/api";
import { optimizeImage } from "../../data/image";

export default function GalleryEditor({ logout }) {
  const { draft, setDraft, saving, save } = useSection("gallery", logout);
  const items = draft || [];
  const [uploading, setUploading] = useState(false);

  const setCap = (i, v) => setDraft(items.map((g, idx) => (idx === i ? { ...g, cap: v } : g)));
  const remove = (i) => setDraft(items.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = items.slice();
    [next[i], next[j]] = [next[j], next[i]];
    setDraft(next);
  };

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const optimized = await optimizeImage(file);
        const name = (file.name || "foto").replace(/\.[^.]+$/, "") + ".jpg";
        const fd = new FormData();
        fd.append("file", optimized, optimized === file ? file.name || "foto" : name);
        const res = await authFetch("/admin/media", { method: "POST", raw: fd });
        uploaded.push({ img: res.url, cap: "" });
      }
      setDraft([...(draft || []), ...uploaded]);
      toast.success(`${uploaded.length} foto caricata/e — ricorda di salvare`);
    } catch (err) {
      toast.error(err.detail || "Caricamento non riuscito");
      if (err.status === 401 && logout) logout();
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div>
      <SaveBar title="Galleria" description="Le foto dei lavori realizzati. Carica, riordina, poi salva." saving={saving} onSave={save} />

      <label className="inline-flex items-center gap-2 cursor-pointer bg-gold text-navy px-5 py-3 text-xs uppercase tracking-[0.18em] hover:bg-gold-soft transition-colors mb-8">
        {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
        {uploading ? "Carico…" : "Carica foto"}
        <input type="file" accept="image/*" multiple onChange={onFiles} disabled={uploading} className="hidden" />
      </label>

      {items.length === 0 ? (
        <p className="text-navy/40 text-sm">Nessuna foto. Carica le prime immagini qui sopra.</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map((g, i) => (
            <figure key={i} className="bg-white border border-navy/10">
              <div className="aspect-square overflow-hidden bg-navy/5">
                <img src={g.img} alt={g.cap} className="w-full h-full object-cover" />
              </div>
              <div className="p-3 space-y-2">
                <input
                  value={g.cap || ""}
                  onChange={(e) => setCap(i, e.target.value)}
                  placeholder="Didascalia"
                  className="w-full bg-transparent border-b border-navy/15 py-1.5 text-xs text-navy placeholder:text-navy/30 focus:outline-none focus:border-gold"
                />
                <div className="flex items-center justify-between">
                  <div className="flex gap-1">
                    <button onClick={() => move(i, -1)} disabled={i === 0} aria-label="Sinistra" className="p-1 text-navy/35 hover:text-navy disabled:opacity-25"><ChevronLeft size={16} /></button>
                    <button onClick={() => move(i, 1)} disabled={i === items.length - 1} aria-label="Destra" className="p-1 text-navy/35 hover:text-navy disabled:opacity-25"><ChevronRight size={16} /></button>
                  </div>
                  <button onClick={() => remove(i)} aria-label="Elimina" className="p-1 text-navy/30 hover:text-red-500"><Trash2 size={16} /></button>
                </div>
              </div>
            </figure>
          ))}
        </div>
      )}
    </div>
  );
}
